<script setup>
// NOTICE: KAPTION-INTEGRATION
// smart-assignment.CONTRACT.1 / smart-assignment.FLAG.3 — the account settings section for
// the delegated assignment resolver: the endpoint URL, and the shared secret the platform
// signs its requests with. Self-gated by KAPTION_SMART_ASSIGNMENT, the same shape
// KaptionTeamAssignmentResolution and KaptionTeamFilterScope use.
//
// smart-assignment.AUTH.5 — the secret field is write-only. The API never returns the
// secret (it is not in `settings` and not in the account payload), so there is nothing to
// prefill and nothing to round-trip; all the screen knows is whether one is stored. Leaving
// the field empty therefore has to mean "keep the one you have" — otherwise every save of
// the URL alone would silently disarm the feature.
//
// smart-assignment.AUTH.5 — the two fields live in a dialog rather than inline on the
// settings page. Account settings is a page of switches an admin flips while scanning; an
// endpoint and a credential are neither, and typing a secret into a form that also holds a
// dozen unrelated toggles invites saving it by accident. The section states what is
// configured and nothing else, which also keeps the endpoint readable without opening
// anything.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import SectionLayout from './SectionLayout.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const url = ref('');
const secret = ref('');
// NOTICE: KAPTION-INTEGRATION — smart-availability.CONTRACT.1 / .UI.6
const availabilityUrl = ref('');
const showCounts = ref('admins');
const isSubmitting = ref(false);
const dialogRef = ref(null);

const { currentAccount, updateAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isFlagEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_SMART_ASSIGNMENT')
);

const storedUrl = computed(
  () => currentAccount.value?.settings?.smart_assignment_url || ''
);

const hasStoredSecret = computed(
  () => !!currentAccount.value?.smart_assignment_secret_present
);
// smart-availability.CONTRACT.1 — the read-only sibling: configured when it has a URL and
// the shared secret above is stored.
const storedAvailabilityUrl = computed(
  () => currentAccount.value?.settings?.smart_availability_url || ''
);
const storedShowCounts = computed(
  () =>
    currentAccount.value?.settings?.smart_availability_show_counts || 'admins'
);
const isAvailabilityConfigured = computed(
  () => !!storedAvailabilityUrl.value && hasStoredSecret.value
);
const showCountsOptions = computed(() => [
  {
    value: 'admins',
    label: t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SHOW_COUNTS.ADMINS'),
  },
  {
    value: 'everyone',
    label: t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SHOW_COUNTS.EVERYONE'),
  },
]);

// The feature only runs when BOTH halves are set, so the summary reports that pair rather
// than either field on its own — a configured URL with no secret sends nothing.
const isConfigured = computed(() => !!storedUrl.value && hasStoredSecret.value);

const secretPlaceholder = computed(() =>
  hasStoredSecret.value
    ? t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SECRET.STORED_PLACEHOLDER')
    : t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SECRET.PLACEHOLDER')
);

watch(
  currentAccount,
  () => {
    url.value = storedUrl.value;
    availabilityUrl.value = storedAvailabilityUrl.value;
    showCounts.value = storedShowCounts.value;
  },
  { deep: true, immediate: true }
);

const openDialog = () => {
  // Reopen on the stored values, never on whatever a cancelled edit left behind.
  url.value = storedUrl.value;
  availabilityUrl.value = storedAvailabilityUrl.value;
  showCounts.value = storedShowCounts.value;
  secret.value = '';
  dialogRef.value.open();
};

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    const payload = {
      smart_assignment_url: url.value,
      // smart-availability.CONTRACT.1 / .UI.6
      smart_availability_url: availabilityUrl.value,
      smart_availability_show_counts: showCounts.value,
    };
    // Only send the secret when the admin actually typed a new one: a blank field means
    // "leave the stored one alone", and sending an empty string would be indistinguishable.
    if (secret.value) {
      payload.smart_assignment_secret = secret.value;
    }
    await updateAccount(payload, { silent: true });
    secret.value = '';
    useAlert(t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.API.SUCCESS'));
    dialogRef.value.close();
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    v-if="isFlagEnabled"
    :title="t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.DESCRIPTION')"
    with-border
  >
    <div class="flex flex-wrap items-center gap-3">
      <div class="grid gap-1 min-w-0 flex-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            isConfigured
              ? t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.STATUS.CONFIGURED')
              : t(
                  'GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.STATUS.NOT_CONFIGURED'
                )
          }}
        </span>
        <span v-if="storedUrl" class="text-n-slate-11 text-xs truncate">
          {{ storedUrl }}
        </span>
        <!-- NOTICE: KAPTION-INTEGRATION — smart-availability.CONTRACT.1 -->
        <span v-if="storedUrl" class="text-n-slate-11 text-xs truncate">
          {{
            isAvailabilityConfigured
              ? t(
                  'GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.AVAILABILITY_STATUS.CONFIGURED'
                )
              : t(
                  'GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.AVAILABILITY_STATUS.NOT_CONFIGURED'
                )
          }}
        </span>
      </div>
      <NextButton
        faded
        slate
        :label="t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.CONFIGURE_BUTTON')"
        @click="openDialog"
      />
    </div>

    <Dialog
      ref="dialogRef"
      :title="t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.TITLE')"
      :description="t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.DESCRIPTION')"
      :confirm-button-label="
        t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.UPDATE_BUTTON')
      "
      :is-loading="isSubmitting"
      @confirm="handleSubmit"
    >
      <div class="grid gap-4">
        <label class="grid gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.URL.LABEL') }}
          </span>
          <NextInput
            v-model="url"
            type="url"
            class="w-full"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.URL.PLACEHOLDER')
            "
          />
          <span class="text-n-slate-11 text-xs">
            {{ t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.URL.HELP') }}
          </span>
        </label>
        <label class="grid gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SECRET.LABEL') }}
          </span>
          <NextInput
            v-model="secret"
            type="password"
            autocomplete="off"
            class="w-full"
            :placeholder="secretPlaceholder"
          />
          <span class="text-n-slate-11 text-xs">
            {{
              hasStoredSecret
                ? t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SECRET.STORED_HELP')
                : t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SECRET.HELP')
            }}
          </span>
        </label>
        <!-- NOTICE: KAPTION-INTEGRATION — smart-availability.CONTRACT.1 / .UI.6 -->
        <label class="grid gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.AVAILABILITY_URL.LABEL')
            }}
          </span>
          <NextInput
            v-model="availabilityUrl"
            type="url"
            class="w-full"
            :placeholder="
              t(
                'GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.AVAILABILITY_URL.PLACEHOLDER'
              )
            "
          />
          <span class="text-n-slate-11 text-xs">
            {{
              t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.AVAILABILITY_URL.HELP')
            }}
          </span>
        </label>
        <label class="grid gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SHOW_COUNTS.LABEL') }}
          </span>
          <select v-model="showCounts" class="w-full text-sm">
            <option
              v-for="option in showCountsOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span class="text-n-slate-11 text-xs">
            {{ t('GENERAL_SETTINGS.FORM.SMART_ASSIGNMENT.SHOW_COUNTS.HELP') }}
          </span>
        </label>
      </div>
    </Dialog>
  </SectionLayout>
</template>
